/**
 * 내 서류 — 기사가 본인 면허증·자격증 사본을 확인한다.
 *
 * 백엔드는 처음부터 허용돼 있었다: `fileActor` 가 기사 클레임을 보고 **본인 것만** 내려준다
 * (`ownerId !== 본인 driver_id` 면 거부). 그래서 담당자 화면과 **같은 callable** 을 쓴다 —
 * 기사 전용 엔드포인트를 따로 만들면 게이트가 두 벌이 되어 언젠가 갈라진다.
 *
 * ⚠️ **기사는 올리거나 지우지 못한다.** 업로드는 `requireDispatch`(기준정보 담당자)다.
 *    기사가 자기 서류를 갈아끼울 수 있으면 그건 서류 검증 자체가 무의미해진다.
 *    화면에도 그 사실을 적어 둔다 — 없는 서류를 보고 "여기서 올리면 되나" 하고 헤매지 않게.
 *
 * 파일은 눌러야 받는다(평균 2.5MB). 담당자 화면과 같은 이유다 —
 * 목록을 열 때마다 다 받으면 데이터도 시간도 버린다.
 */

import { useEffect, useState } from 'react';
import { driverApi, driverError, type DriverFile } from '../lib/driverApi';
import { fetchFileAssetUrl } from '../lib/api';
import { useDriverAuth } from '../lib/driverAuth';
import Screen, { Empty, ErrorBanner, Loading } from './Screen';

const KB = 1024;
const fmtSize = (n: number) => (n >= KB * KB ? `${(n / KB / KB).toFixed(1)}MB` : `${Math.round(n / KB)}KB`);

/**
 * mode = 'car' — 지금 편성된 차량의 **자동차등록증·보험등록증**(2026-09-29 way 「기사앱에서 차량등록증·보험증서
 * 볼 메뉴가 없어졌다」). 차량이 여럿이면 차량번호별로 묶는다. 서버가 편성·서류 종류를 다시 거른다(`fileAsset.ts`).
 */
type Group = { key: string; title: string | null; items: DriverFile[] };

export default function Docs({ mode = 'mine' }: { mode?: 'mine' | 'car' }) {
  const { driver, allocs, fleet } = useDriverAuth();
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ item: DriverFile; url: string } | null>(null);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const driverId = driver?.driver_id;
  // 차주면 자기 차량의 다른 기사가 모는 차까지(#258 「본인 차량 전체」 · 서버 `driverHasCar` 가 같은 규칙으로 다시 본다).
  const cars = [...new Map([
    ...allocs.filter((a) => a.car_id).map((a) => [a.car_id as number, a.car_no] as const),
    ...fleet.flatMap((f) => f.cars).filter((c) => c.car_id).map((c) => [Number(c.car_id), c.car_no] as const),
  ]).entries()];
  const carKey = cars.map(([id]) => id).join(',');

  useEffect(() => {
    if (!driverId) return;
    let alive = true;
    const jobs: Promise<Group>[] = mode === 'car'
      ? cars.map(([carId, carNo]) => driverApi.listMyFiles({ ownerType: 'car', ownerId: carId })
          .then((r) => ({ key: `c${carId}`, title: carNo ?? `차량 ${carId}`, items: r.items })))
      : [driverApi.listMyFiles({ ownerType: 'driver', ownerId: driverId })
          .then((r) => ({ key: 'me', title: null, items: r.items }))];
    Promise.all(jobs)
      .then((gs) => { if (alive) setGroups(gs); })
      .catch((e) => {
        if (!alive) return;
        setError(driverError(e, '서류를 불러오지 못했습니다.'));
        setGroups([]);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 차량 목록은 carKey 로 본다(배열은 렌더마다 새로 만든다)
  }, [driverId, mode, carKey]);

  const total = groups?.reduce((n, g) => n + g.items.length, 0) ?? 0;

  // 열었던 blob URL 은 반드시 해제한다 — 안 하면 탭이 메모리를 계속 문다.
  useEffect(() => () => { if (open) URL.revokeObjectURL(open.url); }, [open]);

  function close() {
    setOpen((cur) => { if (cur) URL.revokeObjectURL(cur.url); return null; });
  }

  async function show(item: DriverFile) {
    setLoadingId(item.fileAssetId);
    setError(null);
    try {
      const url = await fetchFileAssetUrl(item.fileAssetId);
      setOpen((cur) => { if (cur) URL.revokeObjectURL(cur.url); return { item, url }; });
    } catch (e) {
      setError(driverError(e, '서류를 열지 못했습니다.'));
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <Screen title={mode === 'car' ? '차량등록증·보험증' : '내 서류'}>
      {error && <ErrorBanner>{error}</ErrorBanner>}

      {groups === null ? (
        <Loading />
      ) : mode === 'car' && cars.length === 0 ? (
        <Empty>지금 편성된 차량이 없습니다.</Empty>
      ) : total === 0 && !error ? (
        <Empty>등록된 서류가 없습니다.</Empty>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.key}>
              {g.title && <h2 className="mb-2 px-1 text-base font-bold text-ink-soft">{g.title}</h2>}
              {g.items.length === 0 ? (
                <p className="rounded-xl bg-canvas px-4 py-3 text-base text-ink-faint">등록된 서류가 없습니다.</p>
              ) : (
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it.fileAssetId}>
                      <button
                        className="driver-row flex min-h-touch w-full items-center justify-between gap-3 px-5 py-4"
                        onClick={() => show(it)}
                        disabled={loadingId === it.fileAssetId}
                      >
                        <span className="text-lg font-semibold text-ink">{it.label}</span>
                        <span className="text-base text-ink-faint">
                          {loadingId === it.fileAssetId ? '여는 중…' : fmtSize(it.byteSize)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {groups !== null && (
        <p className="mt-6 rounded-xl bg-canvas px-4 py-4 text-base text-ink-soft">
          서류 등록·교체는 <b>사무실에서</b> 합니다. 바뀐 서류가 있으면 담당자에게 알려 주세요.
        </p>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/85 p-3"
          role="dialog"
          aria-label={open.item.label}
          onClick={close}
        >
          <div className="mb-3 flex items-center gap-3 text-white" onClick={(e) => e.stopPropagation()}>
            <span className="text-lg font-bold">{open.item.label}</span>
            <button
              type="button"
              className="ml-auto min-h-touch rounded-xl border-2 border-white/30 px-4 text-base text-white"
              onClick={close}
            >
              닫기
            </button>
          </div>
          <img
            src={open.url}
            alt={open.item.label}
            className="mx-auto max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Screen>
  );
}
